import apiClient from '../services/apiClient';
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import StakeTable from '../pages/GameEngine/StakeTable';
import { Column } from "../pages/GameEngine/StakeTable";



interface house {
  uid: string;
  name: string;
  btc: number;
  eth: number;
  ltc: number;
  bch: number;
  usdt: number;
  trx: number;
  doge: number;
  ada: number;
  xrp: number;
  bnb: number;
  usdp: number;
  nexo: number;
  mkr: number;
  tusd: number;
  usdc: number;
  busd: number;
  nc: number;
  inr: number;
  shib: number;
  matic: number;
}

const Wallet: React.FC = () => {
  const queryClient = useQueryClient();

  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<Record<string, string>>({});
  const currencies = ['inr'];

  const { data: transferDetails = [], isLoading } = useQuery<house[]>({
    queryKey: ['wallets'],
    queryFn: async () => {
      const response = await fetch(`/getwallet`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.json();
    }
  });

  const handleCurrencyChange = (uid: string, currency: string) => {
    setSelectedCurrency(prev => ({ ...prev, [uid]: currency }));
  };

  const updateBalanceMutation = useMutation({
    mutationFn: async ({ uid, coin, amount, type }: { uid: string, coin: string, amount: string, type: "debit" | "credit" }) => {
      return apiClient.post(`/updatebalance`, { uid, coin, amount, type });
    },
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      if (variables.type === 'debit') {
        setDebitValues(prev => ({ ...prev, [variables.uid]: '' }));
      } else {
        setCreditValues(prev => ({ ...prev, [variables.uid]: '' }));
      }
    },
    onError: (error) => {
      console.error('Error updating wallet:', error);
    }
  });

  const handleDebit = (uid: string, coin: string, debitAmount: string) => {
    if (!debitAmount) return;
    updateBalanceMutation.mutate({ uid, coin, amount: debitAmount, type: 'debit' });
  };

  const handleCredit = (uid: string, coin: string, creditAmount: string) => {
    if (!creditAmount) return;
    updateBalanceMutation.mutate({ uid, coin, amount: creditAmount, type: 'credit' });
  };

  const handleInputChange = (uid: string | number, value: string | number, type: 'debit' | 'credit') => {
    const uidStr = String(uid);
    const valueStr = String(value);
    if (type === 'debit') {
      setDebitValues(prevValues => ({ ...prevValues, [uidStr]: valueStr }));
    } else if (type === 'credit') {
      setCreditValues(prevValues => ({ ...prevValues, [uidStr]: valueStr }));
    }
  };

  const filteredUsers = transferDetails.filter((user: any) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.uid.toString().includes(searchTerm)
  );

  const columns: Column[] = [
    { key: 'sn', label: 'S.No', render: (_, __, i) => i + 1 },
    { key: 'name', label: 'Name' },
    { key: 'uid', label: 'UID' },
    {
      key: 'coin', label: 'Coin', render: (_, user) => (
        <select
          value={selectedCurrency[user.uid] || 'inr'}
          onChange={(e) => handleCurrencyChange(user.uid, e.target.value)}
          className="bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {currencies.map((currency) => (
            <option key={currency} value={currency}>{currency.toUpperCase()}</option>
          ))}
        </select>
      )
    },
    { key: 'inr', label: 'Balance', render: (val) => val },
    {
      key: 'debit', label: 'Debit', render: (_, user) => (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={debitValues[user.uid] || ''}
            onChange={(e) => handleInputChange(user.uid, e.target.value, 'debit')}
            placeholder="Amount"
            className="p-1.5 bg-[#1F2937] border border-gray-600 rounded text-white text-xs w-24 outline-none focus:border-red-500"
          />
          <button
            onClick={() => handleDebit(user.uid, selectedCurrency[user.uid] || 'inr', debitValues[user.uid] || '0')}
            disabled={updateBalanceMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white py-1.5 px-3 rounded text-xs font-bold transition-colors disabled:opacity-50"
          >
            Debit
          </button>
        </div>
      )
    },
    {
      key: 'credit', label: 'Credit', render: (_, user) => (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={creditValues[user.uid] || ''}
            onChange={(e) => handleInputChange(user.uid, e.target.value, 'credit')}
            placeholder="Amount"
            className="p-1.5 bg-[#1F2937] border border-gray-600 rounded text-white text-xs w-24 outline-none focus:border-green-500"
          />
          <button
            onClick={() => handleCredit(user.uid, selectedCurrency[user.uid] || 'inr', creditValues[user.uid] || '0')}
            disabled={updateBalanceMutation.isPending}
            className="bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 rounded text-xs font-bold transition-colors disabled:opacity-50"
          >
            Credit
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-6 text-white min-h-screen bg-[#151929]">
      <h2 className="text-3xl font-bold mb-6">User Wallets</h2>
      <div className="mb-6">
        <StakeTable
          title="Wallets"
          columns={columns}
          data={filteredUsers}
          searchPlaceholder="Search by name or UID..."
          onSearch={(val) => setSearchTerm(val)}
          emptyMessage={isLoading ? "Loading wallets..." : "No users found"}
          hasSidebarMargin={false}
        />
      </div>
    </div>
  );
};

export default Wallet;
